import {
  Box,
  SimpleGrid,
  Button,
  Icon,
  Text,
  Badge,
  Tooltip,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { FormInput } from 'src/components/FormInput';
import { FormSelect } from 'src/components/FormSelect';
import { Info } from 'phosphor-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { UFS } from 'src/constants/ufs';
import {
  REGIME_TRIBUTARIO,
  REMETENTE,
  TIPO_NOTA,
  NATUREZA_OPERACAO,
  FINALIDADE,
  DESTINATARIO,
  ORIGEM_PRODUTO,
} from 'src/constants/classiferData';
import { classifierUniqueSchema } from 'src/schema/ClassiferUnique';
import { apiInstance } from 'src/services/api';

interface FormData {
  nome_regra: string;
  data_vigencia: string;
  ncm: string;
  cean: string;
  origem: string;
  tipo_irpj: string;
  remetente: string;
  uf_origem: string;
  identificador_operacao: string;
  nat_op: string;
  finalidade: string;
  destinatario: string;
  uf_destino: string;
}

// Função para remover datas entre parênteses da mensagem
const removeDateFromMessage = (message: string): string => {
  if (!message) return message;
  // Remove padrões como (2026-01-01), (2026/01/01), etc.
  return message
    .replace(/\s*\([0-9]{4}[-/][0-9]{1,2}[-/][0-9]{1,2}\)\s*/g, '')
    .trim();
};

// Componente para exibir o resultado formatado
const ResultDisplay = ({ data }: { data: any }) => {
  if (!data) return null;

  return (
    <Box width="100%" sx={{ pb: '100px' }}>
      {/* Mensagem de sucesso */}
      <Box
        bg={data.regra_atual?.mensagem ? '#73a3f0' : '#e8f5e9'}
        color={data.regra_atual?.mensagem ? '#033380' : '#2e7d32'}
        p={4}
        borderRadius="6px"
        mb={data.regra_atual?.mensagem ? 4 : 6}
        borderLeft={
          data.regra_atual?.mensagem ? '4px solid #0256db' : '4px solid #2e7d32'
        }
      >
        {data.regra_atual?.mensagem ? (
          <Text>
            ⚠️ Consulta sem resultados. Revise os dados informados e tente
            novamente.
          </Text>
        ) : (
          <Text>✅ Consulta realizada com sucesso!</Text>
        )}
      </Box>

      {/* Mensagem de warning se existir */}
      {data.regra_atual?.mensagem && (
        <Box
          bg="#fff3cd"
          color="#856404"
          p={4}
          borderRadius="6px"
          mb={6}
          borderLeft="4px solid #ffc107"
        >
          <Text fontWeight="bold" mb={2}>
            ⚠️ Aviso
          </Text>
          <Text>{removeDateFromMessage(data.regra_atual.mensagem)}</Text>
        </Box>
      )}

      {/* Informações do Produto */}
      {data.regra_atual && !data.regra_atual?.mensagem && (
        <>
          {data.regra_atual.NCM && (
            <Box bg="#3971c6" color="white" p={5} borderRadius="6px" mb={6}>
              <Text fontSize="1.3rem" fontWeight="bold" mb={4}>
                📦 Informações do Produto
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                {data.regra_atual.NCM && (
                  <Box
                    bg="rgba(255, 255, 255, 0.1)"
                    p={3}
                    borderRadius="6px"
                    border="1px solid rgba(255, 255, 255, 0.2)"
                  >
                    <Text fontSize="0.85rem" opacity={0.9} mb={1}>
                      NCM
                    </Text>
                    <Text fontSize="1.1rem" fontWeight="600">
                      {data.regra_atual.NCM}
                    </Text>
                  </Box>
                )}
                {data.regra_atual.cod_prod && (
                  <Box
                    bg="rgba(255, 255, 255, 0.1)"
                    p={3}
                    borderRadius="6px"
                    border="1px solid rgba(255, 255, 255, 0.2)"
                  >
                    <Text fontSize="0.85rem" opacity={0.9} mb={1}>
                      Código Produto
                    </Text>
                    <Text fontSize="1.1rem" fontWeight="600">
                      {data.regra_atual.cod_prod}
                    </Text>
                  </Box>
                )}
                {data.regra_atual.EAN && (
                  <Box
                    bg="rgba(255, 255, 255, 0.1)"
                    p={3}
                    borderRadius="6px"
                    border="1px solid rgba(255, 255, 255, 0.2)"
                  >
                    <Text fontSize="0.85rem" opacity={0.9} mb={1}>
                      EAN
                    </Text>
                    <Text fontSize="1.1rem" fontWeight="600">
                      {data.regra_atual.EAN}
                    </Text>
                  </Box>
                )}
                {data.regra_atual.ex_tipi && (
                  <Box
                    bg="rgba(255, 255, 255, 0.1)"
                    p={3}
                    borderRadius="6px"
                    border="1px solid rgba(255, 255, 255, 0.2)"
                  >
                    <Text fontSize="0.85rem" opacity={0.9} mb={1}>
                      Ex TIPI
                    </Text>
                    <Text fontSize="1.1rem" fontWeight="600">
                      {data.regra_atual.ex_tipi}
                    </Text>
                  </Box>
                )}
              </SimpleGrid>
            </Box>
          )}

          {/* Regra Atual - Tributação */}
          {(data.regra_atual.icms ||
            data.regra_atual.ipi ||
            data.regra_atual.pis ||
            data.regra_atual.cofins) && (
            <Box mb={8}>
              <Text
                fontSize="1.5rem"
                color="#3971c6"
                fontWeight="bold"
                mb={5}
                pb={2}
                borderBottom="2px solid #3971c6"
              >
                📊 Regra Atual - Tributação
              </Text>

              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 4 }}
                spacing={5}
                mb={6}
              >
                {/* ICMS */}
                {data.regra_atual.icms && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #3971c6"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.3rem" fontWeight="700" color="#303030">
                        ICMS
                      </Text>
                      {data.regra_atual.icms.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          CST: {data.regra_atual.icms.cst}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_atual.icms.aliquota && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Alíquota:
                        </Text>
                        <Text
                          color="#3971c6"
                          fontWeight="700"
                          fontSize="1.1rem"
                          flex={2}
                          textAlign="right"
                        >
                          {data.regra_atual.icms.aliquota}%
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.icms.p_red_bc && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Redução BC:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.icms.p_red_bc}%
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.icms.bc_composicao && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Composição BC:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.icms.bc_composicao}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.icms.cfop && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          CFOP:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.icms.cfop}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.icms.cfop_entrada && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          CFOP Entrada:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.icms.cfop_entrada}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.icms.percentual_diferimento && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Diferimento:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.icms.percentual_diferimento}%
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.icms.vigencia_de && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Vigência:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.icms.vigencia_de}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.icms.dispositivo_legal && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Dispositivo Legal:
                        </Text>
                        <Text>{data.regra_atual.icms.dispositivo_legal}</Text>
                      </Box>
                    )}
                    {data.regra_atual.icms.inf_adicionais && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Informações Adicionais:
                        </Text>
                        <Text>{data.regra_atual.icms.inf_adicionais}</Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* IPI */}
                {data.regra_atual.ipi && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #333333"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.3rem" fontWeight="700" color="#303030">
                        IPI
                      </Text>
                      {data.regra_atual.ipi.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          CST: {data.regra_atual.ipi.cst}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_atual.ipi.cenq && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          CENQ:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.ipi.cenq}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.ipi.vigencia_de && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Vigência:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.ipi.vigencia_de}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.ipi.dispositivo_legal && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Dispositivo Legal:
                        </Text>
                        <Text>{data.regra_atual.ipi.dispositivo_legal}</Text>
                      </Box>
                    )}
                    {data.regra_atual.ipi.inf_adicionais && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Informações Adicionais:
                        </Text>
                        <Text>{data.regra_atual.ipi.inf_adicionais}</Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* PIS */}
                {data.regra_atual.pis && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #666666"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.3rem" fontWeight="700" color="#303030">
                        PIS
                      </Text>
                      {data.regra_atual.pis.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          CST: {data.regra_atual.pis.cst}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_atual.pis.codigo_natureza_receita && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Natureza Receita:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.pis.codigo_natureza_receita}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.pis.vigencia_de && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Vigência:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.pis.vigencia_de}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.pis.dispositivo_legal && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Dispositivo Legal:
                        </Text>
                        <Text>{data.regra_atual.pis.dispositivo_legal}</Text>
                      </Box>
                    )}
                    {data.regra_atual.pis.inf_adicionais && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Informações Adicionais:
                        </Text>
                        <Text>{data.regra_atual.pis.inf_adicionais}</Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* COFINS */}
                {data.regra_atual.cofins && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #999999"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.3rem" fontWeight="700" color="#303030">
                        COFINS
                      </Text>
                      {data.regra_atual.cofins.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          CST: {data.regra_atual.cofins.cst}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_atual.cofins.codigo_natureza_receita && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Natureza Receita:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.cofins.codigo_natureza_receita}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.cofins.vigencia_de && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Vigência:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_atual.cofins.vigencia_de}
                        </Text>
                      </Box>
                    )}
                    {data.regra_atual.cofins.dispositivo_legal && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Dispositivo Legal:
                        </Text>
                        <Text>{data.regra_atual.cofins.dispositivo_legal}</Text>
                      </Box>
                    )}
                    {data.regra_atual.cofins.inf_adicionais && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Informações Adicionais:
                        </Text>
                        <Text>{data.regra_atual.cofins.inf_adicionais}</Text>
                      </Box>
                    )}
                  </Box>
                )}
              </SimpleGrid>

              {/* Créditos */}
              {(data.regra_atual.credito_icms ||
                data.regra_atual.credito_ipi ||
                data.regra_atual.credito_pis ||
                data.regra_atual.credito_cofins) && (
                <>
                  <Text
                    fontSize="1.2rem"
                    color="#606060"
                    fontWeight="600"
                    mt={8}
                    mb={4}
                  >
                    💳 Créditos
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
                    {data.regra_atual.credito_icms && (
                      <Box
                        bg="white"
                        border="2px solid #e5e5e5"
                        borderRadius="6px"
                        p={5}
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                      >
                        <Text fontSize="1.1rem" fontWeight="700" mb={4}>
                          Crédito ICMS
                        </Text>
                        {data.regra_atual.credito_icms.indicador_credito && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Indicador:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_icms.indicador_credito}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_icms.cst_entrada && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              CST Entrada:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_icms.cst_entrada}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_icms.vigencia_de && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Vigência:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_icms.vigencia_de}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_icms.dispositivo_legal && (
                          <Box
                            bg="#fbfbfb"
                            p={4}
                            borderRadius="8px"
                            mt={4}
                            fontSize="0.9rem"
                            lineHeight="1.6"
                          >
                            <Text fontWeight="bold" color="#303030" mb={2}>
                              Dispositivo Legal:
                            </Text>
                            <Text>
                              {data.regra_atual.credito_icms.dispositivo_legal}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}

                    {data.regra_atual.credito_ipi && (
                      <Box
                        bg="white"
                        border="2px solid #e5e5e5"
                        borderRadius="6px"
                        p={5}
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                      >
                        <Text fontSize="1.1rem" fontWeight="700" mb={4}>
                          Crédito IPI
                        </Text>
                        {data.regra_atual.credito_ipi.indicador_credito && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Indicador:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_ipi.indicador_credito}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_ipi.cst_entrada && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              CST Entrada:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_ipi.cst_entrada}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_ipi.vigencia_de && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Vigência:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_ipi.vigencia_de}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_ipi.dispositivo_legal && (
                          <Box
                            bg="#fbfbfb"
                            p={4}
                            borderRadius="8px"
                            mt={4}
                            fontSize="0.9rem"
                            lineHeight="1.6"
                          >
                            <Text fontWeight="bold" color="#303030" mb={2}>
                              Dispositivo Legal:
                            </Text>
                            <Text>
                              {data.regra_atual.credito_ipi.dispositivo_legal}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}

                    {data.regra_atual.credito_pis && (
                      <Box
                        bg="white"
                        border="2px solid #e5e5e5"
                        borderRadius="6px"
                        p={5}
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                      >
                        <Text fontSize="1.1rem" fontWeight="700" mb={4}>
                          Crédito PIS
                        </Text>
                        {data.regra_atual.credito_pis.indicador_credito && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Indicador:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_pis.indicador_credito}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_pis.cst_entrada && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              CST Entrada:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_pis.cst_entrada}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_pis.vigencia_de && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Vigência:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_pis.vigencia_de}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_pis.dispositivo_legal && (
                          <Box
                            bg="#fbfbfb"
                            p={4}
                            borderRadius="8px"
                            mt={4}
                            fontSize="0.9rem"
                            lineHeight="1.6"
                          >
                            <Text fontWeight="bold" color="#303030" mb={2}>
                              Dispositivo Legal:
                            </Text>
                            <Text>
                              {data.regra_atual.credito_pis.dispositivo_legal}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}

                    {data.regra_atual.credito_cofins && (
                      <Box
                        bg="white"
                        border="2px solid #e5e5e5"
                        borderRadius="6px"
                        p={5}
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                      >
                        <Text fontSize="1.1rem" fontWeight="700" mb={4}>
                          Crédito COFINS
                        </Text>
                        {data.regra_atual.credito_cofins.indicador_credito && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Indicador:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {
                                data.regra_atual.credito_cofins
                                  .indicador_credito
                              }
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_cofins.cst_entrada && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              CST Entrada:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_cofins.cst_entrada}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_cofins.vigencia_de && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Vigência:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_atual.credito_cofins.vigencia_de}
                            </Text>
                          </Box>
                        )}
                        {data.regra_atual.credito_cofins.dispositivo_legal && (
                          <Box
                            bg="#fbfbfb"
                            p={4}
                            borderRadius="8px"
                            mt={4}
                            fontSize="0.9rem"
                            lineHeight="1.6"
                          >
                            <Text fontWeight="bold" color="#303030" mb={2}>
                              Dispositivo Legal:
                            </Text>
                            <Text>
                              {
                                data.regra_atual.credito_cofins
                                  .dispositivo_legal
                              }
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}
                  </SimpleGrid>
                </>
              )}
            </Box>
          )}

          {/* Regra Reforma Tributária */}
          {data.regra_reforma && (
            <Box mb={8}>
              <Text
                fontSize="1.5rem"
                color="#3971c6"
                fontWeight="bold"
                mb={5}
                pb={2}
                borderBottom="2px solid #3971c6"
              >
                🔄 Regra Reforma Tributária
              </Text>

              {data.regra_reforma.mensagem && (
                <Box
                  bg="#fbfbfb"
                  p={4}
                  borderRadius="8px"
                  mb={5}
                  fontSize="0.9rem"
                  lineHeight="1.6"
                >
                  {data.regra_reforma.mensagem}
                </Box>
              )}

              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 5 }}
                spacing={5}
                mb={6}
              >
                {/* IBS UF */}
                {data.regra_reforma.IBSUF && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #3971c6"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.1rem" fontWeight="700" color="#303030">
                        IBS UF
                      </Text>
                      {data.regra_reforma.IBSUF.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          {data.regra_reforma.IBSUF.cst.split(' - ')[0]}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_reforma.IBSUF.cst && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Situação:
                        </Text>
                        <Text flex={2} textAlign="right" fontSize="0.9rem">
                          {data.regra_reforma.IBSUF.cst}
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.IBSUF.cClassTrib && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Classificação:
                        </Text>
                        <Text flex={2} textAlign="right" fontSize="0.85rem">
                          {data.regra_reforma.IBSUF.cClassTrib}
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.IBSUF.pIBSUF && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Alíquota IBS UF:
                        </Text>
                        <Text
                          color="#3971c6"
                          fontWeight="700"
                          fontSize="1.1rem"
                          flex={2}
                          textAlign="right"
                        >
                          {data.regra_reforma.IBSUF.pIBSUF}%
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.IBSUF.bc_composicao && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Composição BC:
                        </Text>
                        <Text flex={2} textAlign="right" fontSize="0.85rem">
                          {data.regra_reforma.IBSUF.bc_composicao}
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.IBSUF.vigencia_de && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Vigência:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_reforma.IBSUF.vigencia_de}
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.IBSUF.dispositivo_legal && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Dispositivo Legal:
                        </Text>
                        <Text>
                          {data.regra_reforma.IBSUF.dispositivo_legal}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* IBS Municipal */}
                {data.regra_reforma.IBSMun && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #333333"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.1rem" fontWeight="700" color="#303030">
                        IBS Municipal
                      </Text>
                      {data.regra_reforma.IBSMun.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          {data.regra_reforma.IBSMun.cst.split(' - ')[0]}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_reforma.IBSMun.cst && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Situação:
                        </Text>
                        <Text flex={2} textAlign="right" fontSize="0.9rem">
                          {data.regra_reforma.IBSMun.cst}
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.IBSMun.pIBSMun && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Alíquota IBS Mun:
                        </Text>
                        <Text
                          color="#3971c6"
                          fontWeight="700"
                          fontSize="1.1rem"
                          flex={2}
                          textAlign="right"
                        >
                          {data.regra_reforma.IBSMun.pIBSMun}%
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.IBSMun.vigencia_de && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Vigência:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_reforma.IBSMun.vigencia_de}
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.IBSMun.dispositivo_legal && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Dispositivo Legal:
                        </Text>
                        <Text>
                          {data.regra_reforma.IBSMun.dispositivo_legal}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* CBS */}
                {data.regra_reforma.CBS && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #666666"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.1rem" fontWeight="700" color="#303030">
                        CBS
                      </Text>
                      {data.regra_reforma.CBS.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          {data.regra_reforma.CBS.cst.split(' - ')[0]}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_reforma.CBS.cst && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Situação:
                        </Text>
                        <Text flex={2} textAlign="right" fontSize="0.9rem">
                          {data.regra_reforma.CBS.cst}
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.CBS.pCBS && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Alíquota CBS:
                        </Text>
                        <Text
                          color="#3971c6"
                          fontWeight="700"
                          fontSize="1.1rem"
                          flex={2}
                          textAlign="right"
                        >
                          {data.regra_reforma.CBS.pCBS}%
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.CBS.vigencia_de && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Vigência:
                        </Text>
                        <Text flex={2} textAlign="right">
                          {data.regra_reforma.CBS.vigencia_de}
                        </Text>
                      </Box>
                    )}
                    {data.regra_reforma.CBS.dispositivo_legal && (
                      <Box
                        bg="#fbfbfb"
                        p={4}
                        borderRadius="8px"
                        mt={4}
                        fontSize="0.9rem"
                        lineHeight="1.6"
                      >
                        <Text fontWeight="bold" color="#303030" mb={2}>
                          Dispositivo Legal:
                        </Text>
                        <Text>{data.regra_reforma.CBS.dispositivo_legal}</Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* IS */}
                {data.regra_reforma.IS && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #999999"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.1rem" fontWeight="700" color="#303030">
                        IS
                      </Text>
                      {data.regra_reforma.IS.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          {data.regra_reforma.IS.cst.split(' - ')[0]}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_reforma.IS.cst && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Situação:
                        </Text>
                        <Text flex={2} textAlign="right" fontSize="0.9rem">
                          {data.regra_reforma.IS.cst}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* IPI Reforma */}
                {data.regra_reforma.IPI && (
                  <Box
                    bg="white"
                    border="2px solid #e5e5e5"
                    borderLeft="4px solid #333333"
                    borderRadius="6px"
                    p={5}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                    >
                      <Text fontSize="1.1rem" fontWeight="700" color="#303030">
                        IPI
                      </Text>
                      {data.regra_reforma.IPI.cst && (
                        <Badge bg="#3971c6" color="white" px={3} py={1}>
                          {data.regra_reforma.IPI.cst.split(' - ')[0]}
                        </Badge>
                      )}
                    </Box>
                    {data.regra_reforma.IPI.cst && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        py={2}
                        borderBottom="1px solid #f0f0f0"
                      >
                        <Text fontWeight="600" color="#606060" flex={1}>
                          Situação:
                        </Text>
                        <Text flex={2} textAlign="right" fontSize="0.9rem">
                          {data.regra_reforma.IPI.cst}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}
              </SimpleGrid>

              {/* Créditos da Reforma */}
              {(data.regra_reforma.credito_IBSUF ||
                data.regra_reforma.credito_IBSMun ||
                data.regra_reforma.credito_CBS ||
                data.regra_reforma.credito_IPI) && (
                <>
                  <Text
                    fontSize="1.2rem"
                    color="#606060"
                    fontWeight="600"
                    mt={8}
                    mb={4}
                  >
                    💳 Créditos (Reforma)
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
                    {data.regra_reforma.credito_IBSUF && (
                      <Box
                        bg="white"
                        border="2px solid #e5e5e5"
                        borderRadius="6px"
                        p={5}
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                      >
                        <Text fontSize="1.1rem" fontWeight="700" mb={4}>
                          Crédito IBSUF
                        </Text>
                        {data.regra_reforma.credito_IBSUF.indicador_credito && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Indicador:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {
                                data.regra_reforma.credito_IBSUF
                                  .indicador_credito
                              }
                            </Text>
                          </Box>
                        )}
                        {data.regra_reforma.credito_IBSUF.cst_entrada && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              CST Entrada:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_reforma.credito_IBSUF.cst_entrada}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}

                    {data.regra_reforma.credito_IBSMun && (
                      <Box
                        bg="white"
                        border="2px solid #e5e5e5"
                        borderRadius="6px"
                        p={5}
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                      >
                        <Text fontSize="1.1rem" fontWeight="700" mb={4}>
                          Crédito IBSMun
                        </Text>
                        {data.regra_reforma.credito_IBSMun
                          .indicador_credito && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Indicador:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {
                                data.regra_reforma.credito_IBSMun
                                  .indicador_credito
                              }
                            </Text>
                          </Box>
                        )}
                        {data.regra_reforma.credito_IBSMun.cst_entrada && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              CST Entrada:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_reforma.credito_IBSMun.cst_entrada}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}

                    {data.regra_reforma.credito_CBS && (
                      <Box
                        bg="white"
                        border="2px solid #e5e5e5"
                        borderRadius="6px"
                        p={5}
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                      >
                        <Text fontSize="1.1rem" fontWeight="700" mb={4}>
                          Crédito CBS
                        </Text>
                        {data.regra_reforma.credito_CBS.indicador_credito && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Indicador:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_reforma.credito_CBS.indicador_credito}
                            </Text>
                          </Box>
                        )}
                        {data.regra_reforma.credito_CBS.cst_entrada && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              CST Entrada:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_reforma.credito_CBS.cst_entrada}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}

                    {data.regra_reforma.credito_IPI && (
                      <Box
                        bg="white"
                        border="2px solid #e5e5e5"
                        borderRadius="6px"
                        p={5}
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                      >
                        <Text fontSize="1.1rem" fontWeight="700" mb={4}>
                          Crédito IPI
                        </Text>
                        {data.regra_reforma.credito_IPI.indicador_credito && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              Indicador:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_reforma.credito_IPI.indicador_credito}
                            </Text>
                          </Box>
                        )}
                        {data.regra_reforma.credito_IPI.cst_entrada && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            py={2}
                            borderBottom="1px solid #f0f0f0"
                          >
                            <Text fontWeight="600" color="#606060" flex={1}>
                              CST Entrada:
                            </Text>
                            <Text flex={2} textAlign="right">
                              {data.regra_reforma.credito_IPI.cst_entrada}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}
                  </SimpleGrid>
                </>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export const TabUnique = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(classifierUniqueSchema),
    defaultValues: {
      data_vigencia: new Date().toISOString().split('T')[0],
      origem: '0',
      tipo_irpj: '135',
      uf_origem: 'SP',
      nat_op: '121',
      finalidade: '202',
      destinatario: '138',
      remetente: '2',
      identificador_operacao: '1', // Saída por padrão
    },
    shouldFocusError: false,
  });

  useEffect(() => {
    // Definir valores padrão
    setValue('data_vigencia', new Date().toISOString().split('T')[0]);
    setValue('origem', '0');
    setValue('tipo_irpj', '135');
    setValue('uf_origem', 'SP');
    setValue('nat_op', '121');
    setValue('finalidade', '202');
    setValue('destinatario', '138');
    setValue('remetente', '2');
    setValue('identificador_operacao', '1'); // Saída por padrão
  }, [setValue]);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const apiData: any = {
        uf_origem: data.uf_origem.trim(),
        origem: data.origem.trim(),
        entrada_saida: data.identificador_operacao?.trim() || '1',
        nat_op: data.nat_op.trim(),
        finalidade: data.finalidade.trim(),
        remetente: data.remetente.trim(),
        destinatario: data.destinatario.trim(),
        tipo_irpj: parseInt(data.tipo_irpj.trim()),
        data_calculo: data.data_vigencia.trim(),
        consulta: 3, // Consulta completa por padrão
      };

      // Prioridade: EAN > NCM
      // Se EAN estiver preenchido, envia apenas EAN
      // Caso contrário, envia NCM
      if (data.cean && data.cean.trim()) {
        apiData.cean = data.cean.trim();
      } else if (data.ncm && data.ncm.trim()) {
        apiData.ncm = data.ncm.trim();
      }

      // Adicionar UF destino se preenchido
      if (data.uf_destino && data.uf_destino.trim()) {
        apiData.uf_destino = data.uf_destino.trim();
      }

      const api = apiInstance(9000);
      const resultData = await api.post(
        '/classtributario/Produto/consultar-produto',
        apiData,
        {
          headers: {
            GatewayLS: '2e44bb6339e6aacd8faeca8fd4e8694e',
          },
        }
      );
      setResult(resultData.data);
    } catch (err: any) {
      if (err.response) {
        const errorData = err.response.data || {};
        setError(
          `Erro na requisição: ${err.response.status} - ${
            err.response.statusText || 'Erro desconhecido'
          }. ${JSON.stringify(errorData)}`
        );
      } else if (err.request) {
        // Erro de rede (sem resposta do servidor)
        setError(
          'Erro de conexão: Não foi possível conectar ao servidor. Verifique sua conexão ou entre em contato com o suporte.'
        );
      } else {
        // Outro tipo de erro
        setError(
          `Erro ao realizar consulta: ${err.message || 'Erro desconhecido'}`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabConsultaBox className="tabconsulta" width="100%">
      <Box as="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Linha 1: Nome da regra e Data da Vigência */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
          <FormInput
            label="Nome da regra"
            placeholder="Digite o nome da regra"
            error={errors.nome_regra}
            {...register('nome_regra')}
          />
          <Box position="relative">
            <FormInput
              label="Data da Vigência"
              type="date"
              error={errors.data_vigencia}
              {...register('data_vigencia')}
            />
            <Tooltip
              label="Utilize este campo para indicar a data de vigência que será usada
                        para buscar as alíquotas correspondentes ao período informado."
              placement="top"
              hasArrow
              arrowSize={10}
              offset={[-20, 10]}
            >
              <Icon
                as={Info}
                position="absolute"
                right="8px"
                top="-8px"
                color="orange.500"
                boxSize={5}
                cursor="pointer"
              />
            </Tooltip>
          </Box>
        </SimpleGrid>

        {/* Linha 2: NCM, EAN/GTIN, Origem do produto */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
          <FormInput
            label="NCM (NCM ou EAN obrigatório) *"
            placeholder="Código NCM"
            maxLength={8}
            error={errors.ncm}
            {...register('ncm')}
          />
          <FormInput
            label="EAN/GTIN (NCM ou EAN obrigatório) *"
            placeholder="Código EAN"
            error={errors.cean}
            {...register('cean')}
          />
          <FormSelect
            label="Origem do produto"
            isRequired
            error={errors.origem}
            {...register('origem')}
          >
            {ORIGEM_PRODUTO.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </SimpleGrid>

        {/* Linha 3: Regime Tributário e Remetente */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
          <FormSelect
            label="Regime Tributário"
            isRequired
            error={errors.tipo_irpj}
            {...register('tipo_irpj')}
          >
            {REGIME_TRIBUTARIO.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Remetente"
            isRequired
            error={errors.remetente}
            {...register('remetente')}
          >
            <option value="">Selecione...</option>
            {REMETENTE.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </SimpleGrid>

        {/* Linha 4: UF Origem e Identificador da Operação */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
          <FormSelect
            label="UF Origem"
            isRequired
            error={errors.uf_origem}
            {...register('uf_origem')}
          >
            <option value="">Selecione...</option>
            {UFS.map((uf) => (
              <option key={uf.value} value={uf.value}>
                {uf.label}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Identificador da Operação"
            isRequired
            error={errors.identificador_operacao}
            {...register('identificador_operacao')}
          >
            {TIPO_NOTA.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </SimpleGrid>

        {/* Linha 5: Natureza da operação e Finalidade */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
          <FormSelect
            label="Natureza da operação"
            isRequired
            error={errors.nat_op}
            {...register('nat_op')}
          >
            <option value="">Selecione...</option>
            {NATUREZA_OPERACAO.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Finalidade"
            isRequired
            error={errors.finalidade}
            {...register('finalidade')}
          >
            <option value="">Selecione...</option>
            {FINALIDADE.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </SimpleGrid>

        {/* Linha 6: Destinatário e UF Destino */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
          <FormSelect
            label="Destinatário"
            isRequired
            error={errors.destinatario}
            {...register('destinatario')}
          >
            <option value="">Selecione...</option>
            {DESTINATARIO.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="UF Destino"
            error={errors.uf_destino}
            {...register('uf_destino')}
            isRequired
          >
            <option value="">Selecione...</option>
            {UFS.map((uf) => (
              <option key={uf.value} value={uf.value}>
                {uf.label}
              </option>
            ))}
          </FormSelect>
        </SimpleGrid>

        {/* Botão Consultar */}
        <Box display="flex" justifyContent="center" mt={4}>
          <Button
            type="submit"
            bg="#166dd7"
            color="white"
            _hover={{ bg: '#1155bb' }}
            px={8}
            py={6}
            fontSize="md"
            fontWeight="600"
            isLoading={loading}
            loadingText="Consultando..."
          >
            Consultar
          </Button>
        </Box>
      </Box>

      {/* Área de Resultado - Fora do formulário para não quebrar layout */}
      {(result || error) && (
        <Box
          mt={8}
          width="100%"
          p={6}
          bg="#fbfbfb"
          borderRadius="8px"
          border="1px solid #e5e5e5"
        >
          {error && (
            <Box
              bg="#ffebee"
              color="#c62828"
              p={4}
              borderRadius="6px"
              mb={4}
              borderLeft="4px solid #c62828"
            >
              <Text fontWeight="bold">Erro:</Text>
              <Text>{error}</Text>
            </Box>
          )}
          {result && <ResultDisplay data={result} />}
        </Box>
      )}
    </TabConsultaBox>
  );
};

const TabConsultaBox = styled(Box)`
  padding-bottom: 100px;
  .chakra-form-control {
    margin-top: 0 !important;
  }
`;

